import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { installApiLanguageParam } from './apiLanguage'
import languagesConfig from './languages.json'

export type LanguageInfo = (typeof languagesConfig.languages)[number]

export const SOURCE_LANGUAGE = languagesConfig.source
/** Langue affichée quand un texte manque dans une langue autre que la source. */
export const FALLBACK_LANGUAGE = languagesConfig.fallback
export const LANGUAGES: LanguageInfo[] = languagesConfig.languages
export const ACTIVE_LANGUAGES = LANGUAGES.filter((l) => l.active)

const STORAGE_KEY = 'lang'

type LocaleModule = { default: Record<string, unknown> }

const localeLoaders = import.meta.glob<LocaleModule>(['./locales/*/*.json', '!./locales/*/*.context.json'])

function namespacesFor(lang: string): Array<[string, () => Promise<LocaleModule>]> {
  const prefix = `./locales/${lang}/`
  return Object.entries(localeLoaders)
    .filter(([path]) => path.startsWith(prefix))
    .map(([path, load]) => [path.slice(prefix.length, -'.json'.length), load])
}

export function findLanguage(code: string | null | undefined): LanguageInfo | undefined {
  if (!code) return undefined
  const base = code.toLowerCase().split(/[-_]/)[0]
  return LANGUAGES.find((l) => l.code === base)
}

function languageForHost(host: string): LanguageInfo | undefined {
  const bare = host.toLowerCase().replace(/^www\./, '')
  return LANGUAGES.find((l) => l.domain === bare)
}

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function store(code: string) {
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {
    // stockage indisponible (navigation privée)
  }
}

/** Langue active dont le domaine n'est pas encore en ligne : servie sur les autres domaines via `?lang=`. */
function hostedElsewhere(info: LanguageInfo | undefined): info is LanguageInfo {
  return Boolean(info?.active && !info.domain_live)
}

/**
 * Domaine d'une langue (trouvetonrefuge.com, findyourhut.com…) → sa langue, sauf choix explicite
 * (`?lang=` ou enregistré) d'une langue dont le domaine n'est pas encore en ligne.
 * Ailleurs (localhost, prévisualisation) : `?lang=` (enregistré) → choix enregistré → langue source.
 */
export function detectLanguage(): string {
  if (typeof window === 'undefined') return SOURCE_LANGUAGE
  const fromHost = languageForHost(window.location.hostname)
  const fromUrl = findLanguage(new URLSearchParams(window.location.search).get('lang'))
  if (fromHost) {
    if (fromUrl?.code === fromHost.code || hostedElsewhere(fromUrl)) {
      store(fromUrl.code)
      return fromUrl.code
    }
    const stored = findLanguage(readStored())
    return hostedElsewhere(stored) ? stored.code : fromHost.code
  }
  if (fromUrl) {
    store(fromUrl.code)
    return fromUrl.code
  }
  return findLanguage(readStored())?.code ?? SOURCE_LANGUAGE
}

/**
 * Liens « aller au site en … » vers les autres langues, sur la même page.
 * Sur un domaine de langue : langues actives seulement, vers leur domaine s'il est en ligne, sinon `?lang=`.
 * Ailleurs : `?lang=`, toutes les langues.
 */
export function languageSwitchLinks(current: string = i18n.language): Array<{ info: LanguageInfo; href: string }> {
  if (typeof window === 'undefined') return []
  const { hostname, pathname, search, hash } = window.location
  const hostLanguage = languageForHost(hostname)
  const onLanguageDomain = Boolean(hostLanguage)
  return LANGUAGES.filter((l) => l.code !== current && (l.active || !onLanguageDomain)).map((info) => {
    const params = new URLSearchParams(search)
    if (onLanguageDomain && info.domain_live && info.code !== hostLanguage?.code) {
      params.delete('lang')
      const query = params.toString()
      return { info, href: `https://${info.domain}${pathname}${query ? `?${query}` : ''}${hash}` }
    }
    params.set('lang', info.code)
    return { info, href: `${pathname}?${params.toString()}${hash}` }
  })
}

function fallbackChain(lang: string): string[] {
  if (lang === SOURCE_LANGUAGE) return [SOURCE_LANGUAGE]
  if (lang === FALLBACK_LANGUAGE) return [FALLBACK_LANGUAGE, SOURCE_LANGUAGE]
  return [lang, FALLBACK_LANGUAGE, SOURCE_LANGUAGE]
}

async function loadLanguages(langs: string[]) {
  const jobs = langs.flatMap((lang) =>
    namespacesFor(lang).map(async ([ns, load]) => {
      if (i18n.hasResourceBundle(lang, ns)) return
      const mod = await load()
      i18n.addResourceBundle(lang, ns, mod.default, true, true)
    }),
  )
  await Promise.all(jobs)
}

function applyDocumentLanguage(lang: string) {
  if (typeof document === 'undefined') return
  const info = findLanguage(lang)
  document.documentElement.lang = info?.locale ?? lang
  if (info && info.code !== SOURCE_LANGUAGE) document.title = info.app_name
}

export async function initI18n(lang: string = detectLanguage()) {
  await i18n.use(initReactI18next).init({
    lng: lang,
    fallbackLng: (code: string) => fallbackChain(code).slice(1),
    supportedLngs: LANGUAGES.map((l) => l.code),
    defaultNS: 'common',
    ns: [],
    resources: {},
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  })
  await loadLanguages(fallbackChain(lang))
  applyDocumentLanguage(lang)
  installApiLanguageParam(() => findLanguage(i18n.language)?.code ?? SOURCE_LANGUAGE, SOURCE_LANGUAGE)
  return i18n
}

export async function setLanguage(code: string) {
  const info = findLanguage(code)
  if (!info) return
  await loadLanguages(fallbackChain(info.code))
  store(info.code)
  await i18n.changeLanguage(info.code)
  applyDocumentLanguage(info.code)
}

export function currentLocale(): string {
  return findLanguage(i18n.language)?.locale ?? findLanguage(SOURCE_LANGUAGE)!.locale
}

/** Table de libellés (code → texte) lue dans la langue courante à chaque accès ; `keys` = clés i18n complètes « ns:Groupe.cle ». */
export function translatedLabels<K extends string | number>(keys: Record<K, string>): Record<K, string> {
  return Object.defineProperties(
    {} as Record<K, string>,
    Object.fromEntries(
      Object.entries<string>(keys).map(([code, key]) => [code, { enumerable: true, get: () => i18n.t(key) }]),
    ),
  )
}

export default i18n
